import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef } from 'react';

export type Point = { x: number; y: number };

export type GestureHandlers = {
  /** The drag begins: the mouse moved past the threshold, or a finger was held still long enough. */
  onStart?: (origin: Point) => void;
  onMove: (point: Point, origin: Point) => void;
  onDrop: (point: Point, origin: Point) => void;
  /** Esc, a cancelled pointer or unmounting during a drag. */
  onCancel: () => void;
  /** Released without dragging. */
  onTap?: (origin: Point) => void;
};

/** Mouse movement that turns a press into a drag (smaller is a click). */
const DRAG_THRESHOLD_PX = 4;
/** A finger moving this far before the long press fires is scrolling the page, not dragging. */
const TOUCH_SLOP_PX = 8;
const LONG_PRESS_MS = 350;

/**
 * Press-drag-release on calendar entries and empty grid for mouse, pen and touch. Mouse and pen drag once moved
 * a few pixels; touch drags after a long press so a swipe still scrolls the grid. Returns `begin`, to call from
 * `onPointerDown` with the handlers for that gesture; one gesture runs at a time.
 */
export function usePointerGesture(): (event: ReactPointerEvent, handlers: GestureHandlers) => void {
  const abortRef = useRef<(() => void) | null>(null);
  useEffect(() => () => abortRef.current?.(), []);

  return useCallback((event: ReactPointerEvent, handlers: GestureHandlers) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    abortRef.current?.();
    const origin = { x: event.clientX, y: event.clientY };
    const pointerId = event.pointerId;
    const touch = event.pointerType === 'touch';
    let active = false;

    const begin = () => {
      active = true;
      handlers.onStart?.(origin);
    };
    const timer = touch ? window.setTimeout(begin, LONG_PRESS_MS) : undefined;

    const onMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const point = { x: e.clientX, y: e.clientY };
      if (!active) {
        const distance = Math.hypot(point.x - origin.x, point.y - origin.y);
        if (touch) {
          if (distance > TOUCH_SLOP_PX) finish();
          return;
        }
        if (distance < DRAG_THRESHOLD_PX) return;
        begin();
      }
      handlers.onMove(point, origin);
    };
    const onUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const wasActive = active;
      finish();
      if (wasActive) handlers.onDrop({ x: e.clientX, y: e.clientY }, origin);
      else handlers.onTap?.(origin);
    };
    const onPointerCancel = (e: PointerEvent) => {
      if (e.pointerId === pointerId) abort();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !active) return;
      // Esc ends the drag only; it must not also close a popover or dialog.
      e.preventDefault();
      e.stopPropagation();
      abort();
    };
    // Once a touch drag has started the page must not scroll underneath it.
    const onTouchMove = (e: TouchEvent) => {
      if (active) e.preventDefault();
    };
    // A long press would otherwise open the browser's context menu.
    const onContextMenu = (e: Event) => {
      if (active || touch) e.preventDefault();
    };

    function finish() {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onPointerCancel);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('contextmenu', onContextMenu);
      abortRef.current = null;
    }
    function abort() {
      const wasActive = active;
      finish();
      if (wasActive) handlers.onCancel();
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onPointerCancel);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('contextmenu', onContextMenu);
    abortRef.current = abort;
  }, []);
}
